import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- Enum定義 ---

export const RequestType = {
  CREATE: '新規申請',
  CHANGE: '変更申請',
  TERMINATE: '解除申請',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RequestType = keyof typeof RequestType;

export const RequestStatus = {
  ADJUSTING: '調整中',
  SUBMITTED: '申請中',
  IN_PROGRESS: '対応中',
  APPROVED: '承認',
  REJECTED: '否決',
  CANCELLED: '取消',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RequestStatus = keyof typeof RequestStatus;

// --- 型定義 ---

/** テンプレート項目入力値 */
export type TemplateItemInput = {
  itemKey: string;
  values: string[];
};

/** main/sub構造（差分表示用） */
export type MainSubValue<T> = {
  main: T | null;
  sub: T | null;
};

/** テンプレート項目DTO（API3507レスポンス用） */
export type TemplateItemDto = {
  mainValues: string[];
  subValues: string[] | null;
};

/** ユーザー参照 */
export type UserRef = {
  userId: string;
  userName: string;
};

/** チェックリスト状態 */
export type ChecklistStatus = {
  checklistId: string;
  label: string;
  sortOrder: number;
  isChecked: boolean;
  checkedBy: string | null;
  checkedByName: string | null;
  checkedAt: string | null;
  isWChecked: boolean;
  wCheckedBy: string | null;
  wCheckedByName: string | null;
  wCheckedAt: string | null;
};

/** 契約申請取得APIレスポンス */
export type ContractRequestDetailResult = {
  contractRequestId: string;
  contractId: string;
  contractSubId: string;
  managementId: string;
  contractManagementId: string | null;
  requestType: RequestType;
  requestStatus: RequestStatus;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  approvedByName: string | null;
  personInChargeId: string;
  personInChargeName: string;
  projectId: string;
  projectName: string;
  managerId: string;
  managerName: string;
  clientId: string;
  clientName: string;
  memberId: string;
  memberName: string;
  partnerId: string;
  partnerName: string;
  prefectureId: MainSubValue<string> | null;
  location: MainSubValue<string> | null;
  contractTypeId: MainSubValue<string> | null;
  contractTypeName: string | null;
  contractStartDate: MainSubValue<string> | null;
  contractEndDate: MainSubValue<string> | null;
  costAmount: MainSubValue<number> | null;
  salesAmount: MainSubValue<number> | null;
  operationRate: MainSubValue<number> | null;
  contractUnitId: MainSubValue<string> | null;
  contractUnitName: string | null;
  adjustmentUpperLimit: MainSubValue<number> | null;
  adjustmentLowerLimit: MainSubValue<number> | null;
  needsOriginSheet: MainSubValue<boolean> | null;
  originSheetDestination: MainSubValue<string> | null;
  remarks: string | null;
  applicants: UserRef[];
  destinations: UserRef[];
  checklists: ChecklistStatus[];
  templateItems: Record<string, TemplateItemDto>;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string;
  updatedBy: string;
  updatedByName: string;
};

/** 契約申請一覧APIレスポンスの1件 */
export type ContractRequestListResult = {
  contractRequestId: string;
  contractId: string;
  managementId: string;
  contractManagementId: string | null;
  requestType: RequestType;
  requestStatus: RequestStatus;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  personInChargeId: string;
  personInChargeName: string;
  projectId: string;
  projectName: string;
  managerId: string;
  managerName: string;
  clientId: string;
  clientName: string;
  memberId: string;
  memberName: string;
  partnerId: string;
  partnerName: string;
  contractTypeId: string | null;
  contractTypeName: string | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  costAmount: number | null;
  salesAmount: number | null;
  needsOriginSheet: boolean | null;
  createdAt: string;
  updatedAt: string;
};

// --- リクエスト型 ---

/** 契約申請登録APIリクエスト (API3501) */
export type CreateContractRequestParams = {
  requestStatus: 'ADJUSTING' | 'SUBMITTED';
  personInChargeId: string;
  projectId: string;
  managerId: string;
  memberId: string;
  prefectureId?: string;
  location?: string;
  contractTypeId?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  costAmount?: number;
  salesAmount?: number;
  operationRate?: number;
  contractUnitId?: string;
  adjustmentUpperLimit?: number;
  adjustmentLowerLimit?: number;
  needsOriginSheet?: boolean;
  originSheetDestination?: string;
  remarks?: string;
  templateItems?: TemplateItemInput[];
  applicantUserIds?: string[];
  destinationUserIds?: string[];
};

/** 契約変更申請登録APIリクエスト (API3502) */
export type ChangeContractRequestParams = {
  contractId: string;
  requestStatus: 'ADJUSTING' | 'SUBMITTED';
  personInChargeId: string;
  prefectureId?: string;
  location?: string;
  contractTypeId?: string;
  contractEndDate?: string;
  validStartDate?: string;
  costAmount?: number;
  salesAmount?: number;
  operationRate?: number;
  contractUnitId?: string;
  adjustmentUpperLimit?: number;
  adjustmentLowerLimit?: number;
  needsOriginSheet?: boolean;
  originSheetDestination?: string;
  remarks?: string;
  templateItems?: TemplateItemInput[];
  applicantUserIds?: string[];
  destinationUserIds?: string[];
};

/** 契約解除申請登録APIリクエスト (API3503) */
export type TerminateContractRequestParams = {
  contractId: string;
  applicantUserIds: string[];
  destinationUserIds: string[];
};

/** 契約申請更新APIリクエスト (API3504) */
export type UpdateContractRequestParams = {
  contractRequestId: string;
  prefectureId?: string;
  location?: string;
  contractTypeId?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  costAmount?: number;
  salesAmount?: number;
  operationRate?: number;
  contractUnitId?: string;
  adjustmentUpperLimit?: number;
  adjustmentLowerLimit?: number;
  needsOriginSheet?: boolean;
  originSheetDestination?: string;
  remarks?: string;
  templateItems?: TemplateItemInput[];
  personInChargeId?: string;
};

/** 契約申請ステータス更新APIリクエスト (API3505) */
export type UpdateContractRequestStatusParams = {
  contractRequestId: string;
  targetStatus: 'SUBMITTED' | 'IN_PROGRESS' | 'REJECTED' | 'CANCELLED';
  validStartDate?: string;
  applicantUserIds?: string[];
  destinationUserIds?: string[];
};

/** 契約申請承認APIリクエスト (API3506) */
export type ApproveContractRequestParams = {
  contractRequestId: string;
};

/** 契約申請取得APIリクエスト (API3507) */
export type GetContractRequestParams = {
  contractRequestId: string;
};

/** 契約申請一覧取得APIリクエスト (API3508) */
export type ListContractRequestParams = {
  contractId?: string;
  projectId?: string;
  memberId?: string;
  partnerId?: string;
  managerId?: string;
  personInChargeId?: string;
  clientId?: string;
  approvedBy?: string;
  requestType?: RequestType;
  requestStatus?: RequestStatus;
  managementId?: string;
  submittedFrom?: string;
  submittedTo?: string;
  approvedFrom?: string;
  approvedTo?: string;
  needsOriginSheet?: boolean;
};

/** 契約申請チェックAPIリクエスト (API3509) */
export type CheckContractRequestParams = {
  contractRequestId: string;
  checklistId: string;
  isChecked: boolean;
  isWChecked: boolean;
};

// --- API関数 ---

/**
 * 契約申請登録API (API3501)
 * @param params.requestStatus - 申請ステータス（必須, enum: ADJUSTING/SUBMITTED）
 * @param params.personInChargeId - 担当者ID（必須, 最大300文字）
 * @param params.projectId - プロジェクトID（必須, 最大64文字）
 * @param params.managerId - 課長ID（必須, 最大300文字）
 * @param params.memberId - 要員ID（必須, 最大64文字）
 * @param params.templateItems - テンプレート項目（任意）
 * @param params.applicantUserIds - 申請者リスト（SUBMITTED時は1件以上必須）
 * @param params.destinationUserIds - 申請先リスト（SUBMITTED時は1件以上必須）
 */
export const createContractRequest = async (
  params: CreateContractRequestParams,
): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_CONTRACT_REQUEST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.contractRequestId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 契約変更申請登録API (API3502)
 * @param params.contractId - 契約ID（必須, 最大64文字）
 * @param params.requestStatus - 申請ステータス（必須, enum: ADJUSTING/SUBMITTED）
 * @param params.personInChargeId - 担当者ID（必須, 最大300文字）
 * @param params.validStartDate - 有効開始日（SUBMITTED時は必須, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.templateItems - テンプレート項目（任意）
 * @param params.applicantUserIds - 申請者リスト（SUBMITTED時は1件以上必須）
 * @param params.destinationUserIds - 申請先リスト（SUBMITTED時は1件以上必須）
 */
export const changeContractRequest = async (
  params: ChangeContractRequestParams,
): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CHANGE_CONTRACT_REQUEST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.contractRequestId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 契約解除申請登録API (API3503)
 * @param params.contractId - 契約ID（必須, 最大64文字）
 * @param params.applicantUserIds - 申請者リスト（必須, 1件以上）
 * @param params.destinationUserIds - 申請先リスト（必須, 1件以上）
 */
export const terminateContractRequest = async (
  params: TerminateContractRequestParams,
): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.TERMINATE_CONTRACT_REQUEST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.contractRequestId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 契約申請更新API (API3504)
 * @param params.contractRequestId - 契約申請ID（必須, 最大64文字）
 * @param params.templateItems - テンプレート項目（任意, 洗い替え）
 * @param params.remarks - 備考（任意, 最大10000文字）
 */
export const updateContractRequest = async (params: UpdateContractRequestParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_CONTRACT_REQUEST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 契約申請ステータス更新API (API3505)
 * @param params.contractRequestId - 契約申請ID（必須, 最大64文字）
 * @param params.targetStatus - ターゲットステータス（必須, enum: SUBMITTED/IN_PROGRESS/REJECTED/CANCELLED）
 * @param params.validStartDate - 有効開始日（SUBMITTED遷移かつCHANGEの場合のみ必須, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.applicantUserIds - 申請者リスト（SUBMITTED遷移時は必須）
 * @param params.destinationUserIds - 申請先リスト（SUBMITTED遷移時は必須）
 */
export const updateContractRequestStatus = async (
  params: UpdateContractRequestStatusParams,
): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_CONTRACT_REQUEST_STATUS, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 契約申請承認API (API3506)
 * @param params.contractRequestId - 契約申請ID（必須, 最大64文字）
 */
export const approveContractRequest = async (
  params: ApproveContractRequestParams,
): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.APPROVE_CONTRACT_REQUEST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 契約申請取得API (API3507)
 * @param params.contractRequestId - 契約申請ID（必須, 最大64文字）
 */
export const getContractRequest = async (
  params: GetContractRequestParams,
): Promise<ContractRequestDetailResult> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.GET_CONTRACT_REQUEST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 契約申請一覧取得API (API3508)
 * @param params.contractId - 契約ID（任意, 最大64文字, 完全一致）
 * @param params.projectId - プロジェクトID（任意, 最大64文字, 完全一致）
 * @param params.memberId - 要員ID（任意, 最大64文字, 完全一致）
 * @param params.partnerId - パートナーID（任意, 最大64文字, 完全一致）
 * @param params.managerId - 課長ID（任意, 最大300文字, 完全一致）
 * @param params.personInChargeId - 担当者ID（任意, 最大300文字, 完全一致）
 * @param params.clientId - 取引先ID（任意, 最大64文字, 完全一致）
 * @param params.approvedBy - 承認者ID（任意, 最大300文字, 完全一致）
 * @param params.requestType - 申請種別（任意, enum: CREATE/CHANGE/TERMINATE, 完全一致）
 * @param params.requestStatus - 申請ステータス（任意, enum, 完全一致）
 * @param params.managementId - 管理ID（任意, 最大50文字, 完全一致）
 * @param params.submittedFrom - 申請日（年度月開始）（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.submittedTo - 申請日（年度月終了）（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.approvedFrom - 承認日（年度月開始）（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.approvedTo - 承認日（年度月終了）（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.needsOriginSheet - 原籍確認シート必要フラグ（任意, trueの申請のみ検索）
 */
export const listContractRequest = async (
  params: ListContractRequestParams,
): Promise<ContractRequestListResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.LIST_CONTRACT_REQUEST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 契約申請チェックAPI (API3509)
 * @param params.contractRequestId - 契約申請ID（必須, 最大64文字）
 * @param params.checklistId - チェックリストID（必須, 最大64文字）
 * @param params.isChecked - チェック済みフラグ（必須, true/false）
 * @param params.isWChecked - Wチェック済みフラグ（必須, true/false）
 */
export const checkContractRequest = async (params: CheckContractRequestParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.CHECK_CONTRACT_REQUEST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
